import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Footer from "@/components/Footer";
import PricingTiers from "@/components/PricingTiers";
import HeroDemo from "@/components/HeroDemo";
import LandingLivePreview from "@/components/LandingLivePreview";
import { ScaledLivePreview } from "@/components/InstagramMockup";
import {
  Users, Megaphone, Sparkles, Zap, Check, ArrowRight,
  Clock, Trophy, Search, CheckCircle2, Instagram,
  type LucideIcon,
} from "lucide-react";

/* ── data ────────────────────────────────────────────── */

const usps = [
  { title: "Mehr Reichweite", desc: "Erreiche mehr Fans und bringe mehr Zuschauer an den Platz.", icon: Users },
  { title: "Fans auf dem Laufenden", desc: "Automatische Updates zu jedem Spiel. Deine Fans verpassen nichts.", icon: Megaphone },
  { title: "100% automatisiert", desc: "Kein Design-Aufwand, keine Vorkenntnisse. Alles läuft von alleine.", icon: Sparkles },
  { title: "Echtzeit-Tore", desc: "Tore aus dem BFV-Ticker als Live-Story in Sekunden.", icon: Zap },
];

const storyTypes = [
  { title: "Spielankündigung", desc: "Automatisch am Vortag des Spiels.", icon: Clock, type: "matchday" },
  { title: "Toralarm", desc: "Bei jedem Tor deines Vereins in Echtzeit.", icon: Zap, type: "goal" },
  { title: "Endergebnis", desc: "Direkt nach Abpfiff mit Torschützen.", icon: Trophy, type: "endergebnis" },
];

const faqs = [
  { q: "Was kostet Torflash?", a: "Die ersten 14 Tage sind kostenlos. Danach kostet S 20 €, M 30 €, L 50 € und das Jugend-Paket 25 € im Monat. Individuell startet bei 70 € im Monat. Monatlich kündbar." },
  { q: "Können wir ohne Kreditkarte zahlen? Wir haben keine Vereinskarte.", a: "Ja, per SEPA-Lastschrift. Ihr hinterlegt einmal die IBAN, der Betrag wird monatlich eingezogen, und nach jeder Zahlung bekommt ihr automatisch eine Rechnung als PDF." },
  { q: "Für welche Vereine ist das verfügbar?", a: "Für alle Vereine, die beim Bayerischen Fußball-Verband (BFV) registriert sind. Von der Kreisklasse bis zur Bayernliga." },
  { q: "Wir haben mehrere Mannschaften. Geht das?", a: "Ja. Erste, Zweite oder Jugend: jede Mannschaft läuft einzeln und bekommt ein eigenes Kennzeichen auf der Grafik, zum Beispiel #erste oder #zweite." },
  { q: "Wie bekomme ich die Stories?", a: "Im Paket S kommen sie per Telegram oder WhatsApp zum eigenen Posten. Ab Paket M werden sie nach der Verbindung automatisch auf Instagram gepostet." },
  { q: "Wie poste ich die Grafik konkret?", a: "Ihr bekommt jede Grafik doppelt: als kurzes Video für die Story und als Bild für den Feed-Beitrag. In Telegram antippen, \"In Galerie speichern\" wählen und in Instagram wie ein normales Foto hochladen. Das dauert etwa zehn Sekunden." },
  { q: "Postet Torflash automatisch auf unserem Instagram-Account?", a: "Ja, ab Paket M. Dafür verbindet ihr euren Instagram-Account einmal nach der Anmeldung. In Paket S postet ihr selbst." },
  { q: "Was passiert nach der Anmeldung? Muss ich noch etwas tun?", a: "Nach der Anmeldung bekommt ihr eine kurze Anleitung per Mail und verbindet Telegram, das dauert etwa zwei Minuten. Danach läuft alles automatisch: Die erste Spielankündigung kommt zwei Tage vor eurem nächsten Spiel." },
  { q: "Brauche ich einen BFV-Liveticker?", a: "Für Spielankündigungen und Endergebnisse nicht. Für Echtzeit-Tormeldungen muss das Spiel im BFV-Liveticker getickert werden." },
  { q: "Kann ich das Design anpassen?", a: "Ja. Du wählst ein Design und legst deine Vereinsfarbe fest. Alle Stories werden automatisch in eurem Branding generiert." },
  { q: "Woher kommen die Spielerfotos?", a: "Von euch. Ihr ladet die Fotos eurer Spieler hoch oder gebt sie frei, Torflash setzt beim Tor automatisch das richtige Foto ein. Gibt es zu einem Spieler kein Foto, erscheint stattdessen euer Wappen. Wir verwenden keine Fotos ohne eure Freigabe." },
  { q: "Wir sind auf Instagram kaum aktiv. Lohnt sich das trotzdem?", a: "Gerade dann. Mit drei Grafiken pro Spiel habt ihr automatisch einen regelmäßigen Auftritt, ohne dass jemand im Verein Zeit dafür aufbringen muss." },
  { q: "Muss ich etwas installieren?", a: "Nein. Du brauchst nur Telegram auf deinem Handy. Alles andere läuft automatisch." },
];

const CLUB_NAME = "SV Beispielkirchen";
const OPPONENT_NAME = "FC Musterstadt";
const LEAGUE_NAME = "Regionalliga Bayern";
const PLAYER_IMAGE_URL = "https://api.torflash.de/tfassets/dcassets/cutouts/jubel-1.png";
const DEMO_HOME_CREST_URL = "https://api.torflash.de/tfassets/crests/demo-svb.svg";
const DEMO_AWAY_CREST_URL = "https://api.torflash.de/tfassets/crests/demo-fcm.svg";
const HERO_CLUB = "SV Beispielkirchen";
const HERO_OPPONENT = "FC Musterstadt";
const HERO_COLOR = "#1B5EAB";
const timelineSteps = [
  { num: "01", title: "Verein auswählen", effort: "30 Sekunden" },
  { num: "02", title: "Farben und Wappen werden übernommen", effort: "automatisch" },
  { num: "03", title: "Design wählen", effort: "1 Klick" },
  { num: "04", title: "Grafiken kommen direkt auf Instagram", effort: "einmal verbinden, dann automatisch" },
];

const weeklyGraphics = [
  { title: "Spielankündigung", timing: "2 Tage vor dem Spiel", benefit: "Mehr Leute am Platz", type: "matchday-post", format: "feed" as const, playback: "still=1", likes: 247, caption: "Spieltag! Kommt vorbei 💙" },
  { title: "Tor-Alert", timing: "Sekunden nach dem Tor", benefit: "Die Reaktion kommt, solange das Spiel läuft", type: "goal", format: "story" as const, playback: "once=1&min=72&homeScore=2&awayScore=1" },
  { title: "Endstand", timing: "direkt nach Abpfiff", benefit: "Ergebnis draußen, bevor es jemand googelt", type: "endergebnis-post", format: "feed" as const, playback: "still=1&homeScore=3&awayScore=1", likes: 214, caption: "Endstand 3:1 💪" },
];

const designColors = [
  { name: "Dunkelblau", value: "#1B5EAB", className: "bg-primary" },
  { name: "Rot", value: "#E30613", className: "bg-club-red" },
  { name: "Grün", value: "#0E8A45", className: "bg-club-green" },
  { name: "Gelb", value: "#FEDF02", className: "bg-club-yellow" },
  { name: "Schwarz", value: "#16161A", className: "bg-club-black" },
];

function livePreviewUrl(design: number, type: string, playback: string, color: string) {
  const params = new URLSearchParams(playback);
  params.delete("still"); params.delete("once"); params.set("scale", "0.5");
  params.set("club", CLUB_NAME);
  params.set("opp", OPPONENT_NAME);
  params.set("crest", DEMO_HOME_CREST_URL);
  params.set("crestAway", DEMO_AWAY_CREST_URL);
  params.set("pc", color);
  params.set("league", LEAGUE_NAME);
  params.set("scorer", "L. Beispiel");
  params.set("playerImage", PLAYER_IMAGE_URL);
  return `https://api.torflash.de/preview/${design}/${type}?${params.toString()}`;
}

function weeklyPreviewUrl(type: string, playback: string, color: string) {
  const params = new URLSearchParams(playback);
  params.delete("still"); params.delete("once"); params.set("scale", "0.5");
  params.set("club", CLUB_NAME);
  params.set("opp", OPPONENT_NAME);
  params.set("crest", DEMO_HOME_CREST_URL);
  params.set("crestAway", DEMO_AWAY_CREST_URL);
  params.set("pc", color);
  params.set("league", LEAGUE_NAME);
  params.set("scorer", "L. Beispiel");
  params.set("playerImage", PLAYER_IMAGE_URL);
  return `https://api.torflash.de/preview/6/${type}?${params.toString()}`;
}

function heroPreviewUrl(type: string) {
  const params = new URLSearchParams("scale=0.5");
  params.set("club", HERO_CLUB);
  params.set("opp", HERO_OPPONENT);
  params.set("league", "Kreisliga");
  params.set("pc", HERO_COLOR);
  params.set("crest", DEMO_HOME_CREST_URL);
  params.set("crestAway", DEMO_AWAY_CREST_URL);
  params.set("scorer", "L. Beispiel");
  params.set("min", "72");
  params.set("homeScore", "2");
  params.set("awayScore", "1");
  return `https://api.torflash.de/preview/1/${type}?${params.toString()}`;
}

function timelinePreviewUrl(design: number, type: "matchday-post" | "goal") {
  const params = new URLSearchParams("scale=0.5");
  params.set("pc", HERO_COLOR);
  params.set("club", HERO_CLUB);
  params.set("opp", HERO_OPPONENT);
  params.set("crest", DEMO_HOME_CREST_URL);
  params.set("crestAway", DEMO_AWAY_CREST_URL);
  params.set("scorer", "L. Beispiel");
  params.set("min", "72");
  params.set("homeScore", "2");
  params.set("awayScore", "1");
  return `https://api.torflash.de/preview/${design}/${type}?${params.toString()}`;
}

function playerPreviewUrl(design: number) {
  const params = new URLSearchParams("scale=0.5&min=72&homeScore=2&awayScore=1");
  params.set("club", CLUB_NAME);
  params.set("opp", OPPONENT_NAME);
  params.set("league", LEAGUE_NAME);
  params.set("pc", HERO_COLOR);
  params.set("crest", DEMO_HOME_CREST_URL);
  params.set("crestAway", DEMO_AWAY_CREST_URL);
  params.set("scorer", "L. Beispiel");
  params.set("playerImage", PLAYER_IMAGE_URL);
  return `https://api.torflash.de/preview/${design}/goal?${params.toString()}`;
}

/* ── helpers ─────────────────────────────────────────── */

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] },
  }),
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

/* Shared typography classes */
const sectionH2 = "mt-4 font-display text-3xl font-extrabold uppercase leading-[1.1] tracking-tight text-foreground md:text-[2.5rem]";
const cardTitle = "text-lg font-bold leading-snug text-foreground tracking-tight";
const bodyText = "text-base leading-[1.65] text-slate-600";

/* ── page ────────────────────────────────────────────── */

export default function LandingPage() {
  const [designColor, setDesignColor] = useState(designColors[0].value);

  return (
    <div className="min-h-screen bg-white text-foreground">

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative -mt-20 overflow-hidden bg-hero-stadium pt-20">
        {/* Subtle pitch geometry: centre circle + halfway line, faded into the light background */}
        <div className="pointer-events-none absolute inset-0 opacity-100">
          <div className="absolute left-1/2 top-[42%] h-[640px] w-[640px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/10" />
          <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-primary/10" />
        </div>

        <div className="container relative z-10 pt-8 pb-12 md:pt-8 md:pb-16">
          <div className="mx-auto max-w-5xl text-center">
            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="font-display text-[clamp(1.5rem,3.9vw,2.3rem)] font-extrabold uppercase leading-[1.1] tracking-tight text-foreground md:whitespace-nowrap"
            >
              Vom Tor zur Story.{" "}
              <span className="text-gradient-blue">In Sekunden.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.12 }}
              className="mx-auto mt-4 mb-6 max-w-2xl text-sm leading-[1.55] text-slate-600 md:text-base"
            >
              Torflash erkennt jedes Tor deines Vereins automatisch und postet
              eine fertige Instagram Story. Live, ohne Aufwand.
            </motion.p>
          </div>

          {/* Die 3 Story-Typen: Spielankündigung, Toralarm, Endergebnis */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mx-auto grid max-w-2xl grid-cols-3 gap-3 md:gap-5"
          >
            {storyTypes.map((st, index) => (
              <div key={st.title} className="flex flex-col items-center">
                <PhoneFrame>
                  <ScaledLivePreview
                    key={`hero-1-${st.type}`}
                    src={heroPreviewUrl(st.type)}
                    title={`${st.title} Vorschau`}
                    height={1920}
                     eager={index < 2}
                  />
                </PhoneFrame>
                <span className="mt-2.5 text-center text-[11px] font-semibold leading-tight text-slate-700 md:text-xs">
                  {st.title}
                </span>
              </div>
            ))}
          </motion.div>

          <p className="mx-auto mt-5 max-w-3xl text-center text-xs leading-relaxed text-muted-foreground">
            Beispielgrafiken mit Beispieldaten.
          </p>


          {/* CTA below the demo */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="mt-6 flex justify-center"
          >
            <Link to="/registrieren">
              <Button variant="hero" size="lg" className="px-7">
                Jetzt starten →
              </Button>
            </Link>
          </motion.div>
        </div>

      </section>

      {/* ═══════════ DESIGNBEWEIS ═══════════ */}
      <section className="cv-auto overflow-hidden bg-background py-14 md:py-28">
        <div className="container text-center">
          <span className="eyebrow">Euer Auftritt</span>
          <h2 className={sectionH2}>16 Designs, eure Farben</h2>
        </div>
        <div className="design-marquee mt-8 overflow-x-auto md:mt-12 md:overflow-hidden">
          <div className="design-marquee-track flex w-max pl-4">
            {[0, 1].map((copy) => (
              <div key={copy} className="flex shrink-0 gap-4 pr-4" aria-hidden={copy === 1 ? "true" : undefined}>
                {Array.from({ length: 16 }, (_, index) => index + 1).map((design) => (
                  <div key={`${copy}-${design}-${designColor}`} className="w-[168px] shrink-0 overflow-hidden rounded-lg border border-border bg-muted shadow-sm md:w-[250px]">
                    <ScaledLivePreview
                      key={`design-${design}-${designColor}`}
                      src={livePreviewUrl(design, "matchday-post", "still=1", designColor)}
                      title={`Design ${design} in Vereinsfarbe`}
                      height={1350}
                    />
                    <div className="bg-background px-3 py-2 text-left text-xs font-bold text-foreground">Design {design}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="container mt-7 flex flex-col items-center md:mt-10">
          <p className="text-center text-sm font-semibold text-foreground">Alle Farben möglich, hier nur 5 Beispiele.</p>
          <div className="mt-3 flex items-center gap-2.5 md:mt-4 md:gap-3" role="group" aria-label="Vereinsfarbe auswählen">
            {designColors.map((color) => (
              <Button
                key={color.name}
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setDesignColor(color.value)}
                aria-label={`${color.name} auswählen`}
                aria-pressed={designColor === color.value}
                className={`h-10 w-10 rounded-full p-1 md:h-12 md:w-12 ${designColor === color.value ? "border-primary ring-2 ring-primary/25" : "border-border"}`}
              >
                <span className={`h-full w-full rounded-full ${color.className}`} />
              </Button>
            ))}
          </div>
          <p className="mt-3 max-w-xl text-center text-xs leading-relaxed text-muted-foreground md:mt-4 md:text-sm">
            Eure Vereinsfarbe wird beim Einrichten automatisch übernommen, Schrift und Kontraste rechnen wir passend dazu.
          </p>
          <Button asChild variant="hero" size="lg" className="mt-5 md:mt-6">
            <Link to="/registrieren">Jetzt Design anpassen <ArrowRight /></Link>
          </Button>
        </div>
      </section>

      {/* ═══════════ VIDEO: IN 2 MINUTEN ERKLÄRT ═══════════ */}
      <section className="bg-background py-14 md:py-24">
        <div className="container">
          <div className="text-center">
            <h2 className={sectionH2}>In 2 Minuten erklärt</h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
              Wie Torflash an einem Spieltag für euren Verein läuft — vom Anpfiff bis zum Endstand.
            </p>
          </div>
          <div className="mx-auto mt-8 w-full max-w-[860px] md:mt-12">
            <div className="relative h-0 w-full overflow-hidden rounded-xl pb-[56.25%] premium-shadow-lg">
              <iframe
                src="https://www.loom.com/embed/b38d61ec82d148f890c4f71efedae15d"
                title="Torflash in 2 Minuten erklärt"
                frameBorder="0"
                allowFullScreen
                className="absolute left-0 top-0 h-full w-full"
                loading="lazy"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ SPIELERFOTOS ═══════════ */}
      <section className="cv-auto border-y border-border bg-primary-light py-12 md:bg-background md:py-28">
        <div className="container grid max-w-6xl items-center gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,620px)] md:gap-14">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.span variants={fadeUp} className="eyebrow">Mit echten Spielerfotos</motion.span>
            <motion.h2 variants={fadeUp} className={`${sectionH2} text-primary md:text-foreground`}>Der Torschütze steht mit im Bild</motion.h2>
            <motion.p variants={fadeUp} className="mt-4 max-w-xl text-sm leading-[1.65] text-muted-foreground md:mt-5 md:text-lg">
              Torflash erkennt, wer getroffen hat, und setzt das Foto eures Spielers automatisch in die Tor-Grafik.
            </motion.p>
            <motion.ul variants={fadeUp} className="mt-6 space-y-2.5 md:mt-8 md:space-y-3">
              {["Fotos kommen aus eurem Kader", "Kein Zuordnen von Hand", "Ohne Foto läuft alles normal weiter"].map((point) => (
                <li key={point} className="flex items-center gap-3 text-sm font-semibold text-foreground md:text-base">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Check className="h-4 w-4" /></span>
                  {point}
                </li>
              ))}
            </motion.ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.55 }}>
            <div className="grid grid-cols-1 items-start gap-5 sm:grid-cols-2">
              {[6, 13].map((design) => (
                <LandingLivePreview
                  key={`player-${design}-${HERO_COLOR}`}
                  src={playerPreviewUrl(design)}
                  title={`Tor-Grafik mit Spielerfoto, Design ${design}`}
                  format="story"
                  clubName={CLUB_NAME}
                  crestUrl={DEMO_HOME_CREST_URL}
                  compact
                />
              ))}
            </div>
            <p className="mx-auto mt-3 max-w-xl text-center text-xs leading-relaxed text-muted-foreground">
              Beispielbild. Im Einsatz steht dort das Foto eures eigenen Torschützen, automatisch aus eurem Kader.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ ABLAUF UND PREIS ═══════════ */}
      <section className="cv-auto border-y border-border bg-muted/45 py-20 md:py-28">
        <div className="container max-w-6xl">
          <div className="text-center">
            <span className="eyebrow">In wenigen Minuten startklar</span>
            <h2 className={sectionH2}>So läuft es ab</h2>
          </div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="relative mt-14"
          >
            <div className="absolute bottom-0 left-5 top-0 w-0.5 bg-border md:bottom-auto md:left-[12.5%] md:right-[12.5%] md:top-5 md:h-0.5 md:w-auto" />
            <motion.div
              variants={{ hidden: { scaleY: 0 }, visible: { scaleY: 1, transition: { duration: 1.1, ease: "easeOut" } } }}
              className="absolute bottom-0 left-5 top-0 w-0.5 origin-top bg-primary md:hidden"
            />
            <motion.div
              variants={{ hidden: { scaleX: 0 }, visible: { scaleX: 1, transition: { duration: 1.1, ease: "easeOut" } } }}
              className="absolute left-[12.5%] right-[12.5%] top-5 hidden h-0.5 origin-left bg-primary md:block"
            />
            <div className="grid gap-12 md:grid-cols-4 md:gap-6">
              {timelineSteps.map((step, index) => (
                <motion.article
                  key={step.num}
                  variants={{
                    hidden: { opacity: 0, y: 24 },
                    visible: { opacity: 1, y: 0, transition: { delay: index * 0.12, duration: 0.5 } },
                  }}
                  className="relative pl-16 md:pl-0 md:pt-16"
                >
                  <span className="absolute left-0 top-0 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground shadow-md md:left-1/2 md:-translate-x-1/2">
                    {step.num}
                  </span>
                  <h3 className="min-h-[44px] text-base font-bold leading-snug text-foreground">{step.title}</h3>
                  <TimelineVisual index={index} />
                  <p className="mt-3 text-sm text-muted-foreground">{step.effort}</p>
                </motion.article>
              ))}
            </div>
          </motion.div>
          <p className="mt-8 text-center text-sm font-semibold text-muted-foreground">Daten kommen direkt vom BFV. Kein Eintippen, kein Login, keine App.</p>

        </div>
      </section>

      {/* ═══════════ VORTEILE ═══════════ */}
      <section className="cv-auto bg-white bg-hero-pattern py-20 md:py-28">
        <div className="container">
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: "-80px" }} variants={stagger}
            className="text-center"
          >
            <motion.span variants={fadeUp} className="eyebrow">Vorteile</motion.span>
            <motion.h2 variants={fadeUp} className={sectionH2}>
              Warum Torflash?
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: "-60px" }} variants={stagger}
            className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {usps.map((u, i) => (
              <motion.div
                key={u.title}
                variants={fadeUp}
                custom={i}
                className="group rounded-2xl bg-white p-7 text-center shadow-[0_1px_2px_hsl(220_20%_10%/0.04),0_4px_16px_-4px_hsl(220_20%_10%/0.04)] transition-shadow hover:shadow-[0_2px_4px_hsl(220_20%_10%/0.05),0_12px_32px_-8px_hsl(215_80%_45%/0.12)]"
              >
                <UspBadge index={i} icon={u.icon} />
                <h3 className={cardTitle}>{u.title}</h3>
                <p className={`mt-2 ${bodyText} text-[15px]`}>{u.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>


      {/* ═══════════ WÖCHENTLICHE GRAFIKEN ═══════════ */}
      <section className="cv-auto bg-muted/45 py-20 md:py-28">
        <div className="container max-w-6xl">
          <div className="text-center">
            <span className="eyebrow">Automatisch geliefert</span>
            <h2 className={sectionH2}>Was ihr jede Woche bekommt</h2>
          </div>
          <div className="-mx-4 mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-4 md:mx-0 md:grid md:grid-cols-3 md:gap-7 md:overflow-visible md:px-0">
            {weeklyGraphics.map((graphic, index) => (
              <article key={graphic.type} className="grid h-full w-[82%] shrink-0 snap-center grid-rows-[auto_1fr_auto] rounded-xl border border-border bg-background p-4 shadow-sm sm:w-[58%] md:w-auto">
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-foreground">{graphic.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{graphic.timing}</p>
                </div>
                <div className="h-[650px] w-full">
                  <LandingLivePreview
                    key={`weekly-${graphic.type}-${designColor}`}
                    src={weeklyPreviewUrl(graphic.type, graphic.playback, designColor)}
                    title={`${graphic.title} Vorschau`}
                    format={graphic.format}
                     clubName={CLUB_NAME}
                    crestUrl={DEMO_HOME_CREST_URL}
                    likes={graphic.likes}
                    caption={graphic.caption}
                    showSocialDetails
                    compact
                   />
                </div>
                <p className="mt-5 flex items-start gap-2 border-t border-border pt-4 text-sm font-semibold text-foreground">
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{graphic.benefit}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ SO FUNKTIONIERT'S (Automatisierungs-Demo) ═══════════ */}
      <section id="hero-demo" className="cv-auto hidden bg-[#F8FAFC] bg-hero-pattern py-20 md:block md:py-28">
        <div className="container">
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: "-80px" }} variants={stagger}
            className="text-center"
          >
            <motion.span variants={fadeUp} className="eyebrow">Automatisierung</motion.span>
            <motion.h2 variants={fadeUp} className={sectionH2}>
              So funktioniert's
            </motion.h2>
            <motion.p variants={fadeUp} className="mx-auto mt-4 max-w-md text-base leading-[1.65] text-slate-600">
              Vom BFV-Liveticker zur fertigen Story – komplett automatisch.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: "-60px" }} variants={fadeUp}
            className="mx-auto mt-12 max-w-[720px]"
          >
            <HeroDemo primaryColor={designColor} />
          </motion.div>
        </div>
      </section>

      {/* ═══════════ PREISE ═══════════ */}
      <section id="preise" className="cv-auto py-20 md:py-28">
        <div className="container max-w-6xl">
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: "-80px" }} variants={stagger}
            className="text-center"
          >
            <motion.span variants={fadeUp} className="eyebrow">Preise</motion.span>
            <motion.h2 variants={fadeUp} className={sectionH2}>
              Preise
            </motion.h2>
            <motion.p variants={fadeUp} className="mx-auto mt-5 max-w-xl text-base leading-[1.65] text-slate-600">
               Erst 14 Tage kostenlos testen, danach monatlich kündbar. Keine Einrichtungsgebühr.
            </motion.p>
          </motion.div>

          <div className="mt-12">
            <PricingTiers asLink />
          </div>
        </div>
      </section>


      {/* ═══════════ FAQ ═══════════ */}
      <section id="faq" className="cv-auto bg-[#F8FAFC] bg-hero-pattern py-20 md:py-28">
        <div className="container max-w-2xl">
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true, margin: "-80px" }} variants={stagger}
            className="text-center"
          >
            <motion.span variants={fadeUp} className="eyebrow">Support</motion.span>
            <motion.h2 variants={fadeUp} className={sectionH2}>
              Häufige Fragen
            </motion.h2>
          </motion.div>

          <Accordion type="single" collapsible className="mt-12">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-border/60">
                <AccordionTrigger className="py-5 text-left text-base font-semibold text-foreground hover:text-primary">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-base leading-[1.65] text-slate-600">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <p className="mt-12 text-center text-xs text-slate-500">
            Torflash ist ein unabhängiges Angebot ohne offizielle Verbindung zum BFV.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TimelineVisual({ index }: { index: number }) {
  const shellClass = "mt-4 flex h-[200px] overflow-hidden rounded-2xl border border-border bg-background p-4 shadow-[0_10px_30px_-18px_hsl(220_20%_10%/0.28)]";

  if (index === 0) {
    return (
      <div className={`${shellClass} flex-col justify-center`}>
        <div className="flex h-10 items-center gap-2 rounded-lg border border-input bg-background px-3 text-sm text-muted-foreground shadow-sm">
          <Search className="h-4 w-4 shrink-0" />
          <span>SV Beispiel…</span>
        </div>
        <div className="mt-2 flex items-center gap-3 rounded-lg border border-primary/25 bg-primary/5 p-3">
          <img src={DEMO_HOME_CREST_URL} alt="Wappen SV Beispielkirchen" className="h-9 w-9 object-contain" />
          <span className="min-w-0 text-left">
            <span className="block text-sm font-bold text-foreground">SV Beispielkirchen</span>
            <span className="block text-xs text-muted-foreground">Kreisliga</span>
          </span>
        </div>
      </div>
    );
  }

  if (index === 1) {
    return (
      <div className={`${shellClass} items-center justify-between gap-3`}>
        <div className="space-y-2">
          {[
            ["bg-primary", "#1B5EAB", "Primär"],
            ["bg-club-yellow", "#FEDF02", "Sekundär"],
            ["bg-background", "#FFFFFF", "Weiß"],
          ].map(([colorClass, hex, label]) => (
            <div key={label} className="flex items-center gap-2">
              <span className={`h-8 w-8 shrink-0 rounded-md border border-border ${colorClass}`} />
              <span className="text-left">
                <span className="block text-[10px] text-muted-foreground">{label}</span>
                <span className="block text-xs font-semibold text-foreground">{hex}</span>
              </span>
            </div>
          ))}
        </div>
        <div className="relative flex h-24 w-20 shrink-0 items-center justify-center rounded-xl bg-muted p-3">
          <img src={DEMO_HOME_CREST_URL} alt="Wappen SV Beispielkirchen" className="h-full w-full object-contain" />
          <CheckCircle2 className="absolute -right-1 -top-1 h-6 w-6 fill-primary text-primary-foreground" />
          <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold text-primary">automatisch erkannt</span>
        </div>
      </div>
    );
  }

  if (index === 2) {
    return (
      <div className={`${shellClass} items-center justify-center gap-2`}>
        {[1, 6, 13].map((design) => (
          <div key={design} className={`relative aspect-[4/5] min-w-0 flex-1 overflow-hidden rounded-md bg-muted ${design === 6 ? "ring-2 ring-primary ring-offset-2" : "border border-border"}`}>
            <ScaledLivePreview
              src={timelinePreviewUrl(design, "matchday-post")}
              title={`Design ${design}`}
              height={1350}
            />
            {design === 6 ? (
              <span className="absolute right-1 top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm">
                <Check className="h-3 w-3" />
              </span>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`${shellClass} items-center gap-4`}>
      {/* Realistisches Handy mit vollständiger 9:16-Story */}
      <div className="relative h-full shrink-0 rounded-[14px] bg-foreground p-[3px] shadow-lg" style={{ aspectRatio: "9 / 17.5" }}>
        <div className="relative h-full w-full overflow-hidden rounded-[11px] bg-foreground">
          <div className="absolute inset-x-0 top-1/2 aspect-[9/16] -translate-y-1/2">
            <ScaledLivePreview src={timelinePreviewUrl(1, "goal")} title="Automatische Tor-Grafik als Instagram-Story" height={1920} />
          </div>
          <div className="absolute inset-x-1.5 top-1.5 z-10 flex gap-0.5">
            <span className="h-[2px] flex-1 rounded-full bg-background" />
            <span className="h-[2px] flex-1 rounded-full bg-background/40" />
          </div>
          <div className="absolute left-1.5 top-3 z-10 flex items-center gap-1">
            <img src={DEMO_HOME_CREST_URL} alt="" className="h-3.5 w-3.5 rounded-full bg-background object-contain p-[1px]" />
            <span className="text-[6px] font-semibold text-background drop-shadow">sv_beispielkirchen</span>
          </div>
        </div>
      </div>
      <div className="min-w-0 flex-1 space-y-2 text-left">
        <div className="flex items-center gap-2">
          <span
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-primary-foreground"
            style={{ background: "linear-gradient(45deg,#F58529,#DD2A7B 55%,#8134AF)" }}
          >
            <Instagram className="h-4 w-4" />
          </span>
          <span className="truncate text-xs font-bold text-foreground">Verbunden</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-primary" /> Story gepostet · 72&apos;
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-primary" /> Ohne Zutun
        </div>
        <p className="text-[10px] font-semibold leading-snug text-primary">
          Einmal Instagram verbinden – danach alles automatisch
        </p>
      </div>
    </div>
  );
}

/* ───────────────── Benefit icon badges (Vorteile section) ───────────────── */

/* Large, centered icon badge above each benefit. Each card gets its own
   distinctive accent so the four benefits don't read as four identical tiles. */
function UspBadge({ index, icon: Icon }: { index: number; icon: LucideIcon }) {
  const shape = index % 2 === 0 ? "rounded-full" : "rounded-2xl";

  return (
    <div className="relative mx-auto mb-6 h-16 w-16 transition-transform duration-300 group-hover:scale-110">
      {/* Unique accent per benefit */}
      {index === 0 && ( // Mehr Reichweite — fan multiplier
        <span className="absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-primary text-[9px] font-bold leading-none text-primary-foreground shadow-sm">
          +2
        </span>
      )}
      {index === 1 && ( // Fans auf dem Laufenden — sound waves
        <>
          <span className="absolute -inset-2 rounded-3xl border border-primary/25" />
          <span className="absolute -inset-4 rounded-[2rem] border border-primary/10" />
        </>
      )}
      {index === 2 && ( // 100% automatisiert — orbiting spark
        <span className="absolute -left-1.5 -top-1.5 h-3.5 w-3.5 rounded-full bg-gradient-blue shadow-sm" />
      )}
      {index === 3 && ( // Echtzeit-Tore — goal pulse
        <span className="absolute inset-0 rounded-2xl bg-primary/25 [animation:ping_2.4s_cubic-bezier(0,0,0.2,1)_infinite]" />
      )}

      <div
        className={`relative flex h-16 w-16 items-center justify-center bg-gradient-blue ${shape} shadow-[0_10px_24px_-8px_hsl(215_80%_45%/0.55)]`}
      >
        <Icon className="h-7 w-7 text-primary-foreground" strokeWidth={1.9} />
      </div>
    </div>
  );
}

/* ───────────────── Light Phone Frame for Hero Story Previews ───────────────── */

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative w-full" style={{ maxWidth: 180 }}>
      <div className="relative rounded-[1.6rem] border-[6px] border-black bg-black shadow-[0_12px_40px_-12px_rgba(15,23,42,0.35)]">
        <div className="absolute left-1/2 top-1.5 z-20 h-1 w-10 -translate-x-1/2 rounded-full bg-neutral-800" />
        <div className="relative overflow-hidden rounded-[1.15rem] bg-black" style={{ aspectRatio: "9/16" }}>
          {children}
        </div>
      </div>
    </div>
  );
}
